﻿import * as classNames from "classnames"
import { SiteLayoutType } from "../../../../pagesPage/stateInterfaces"

interface ArticleVerticalChapterMenuProps {
    siteLayoutType: SiteLayoutType
}

interface ArticleVerticalChapterMenuState {
    chapters: string[]
    chapterElements: HTMLElement[]
    currentChapterIndex: number
    readProgress: number
    isBarVisible: boolean
    isMobileExpanded: boolean
    isMobileDropdownOpen: boolean
    isDesktopSidebarOpen: boolean
}

export class ArticleVerticalChapterMenu extends React.Component<
    ArticleVerticalChapterMenuProps,
    ArticleVerticalChapterMenuState
> {
    public static siteScriptName = "ArticleVerticalChapterMenu"

    private chapterObserver: IntersectionObserver | null = null
    private scrollRAF: number = 0
    private disableObserver: boolean = false
    private retryCount: number = 0
    private containerRef: HTMLDivElement | null = null
    private topStoryEl: HTMLElement | null = null
    private sidebarContentRef: HTMLDivElement | null = null
    private lastScrollY: number = 0
    private paragraphObserver: IntersectionObserver | null = null

    constructor(props: ArticleVerticalChapterMenuProps) {
        super(props)
        this.state = {
            chapters: [],
            chapterElements: [],
            currentChapterIndex: -1,
            readProgress: 0,
            isBarVisible: false,
            isMobileExpanded: false,
            isMobileDropdownOpen: false,
            isDesktopSidebarOpen: false,
        }
    }

    componentDidMount() {
        this.topStoryEl = document.querySelector(".article-magazine-top-story")
        this.detectChapters()
        window.addEventListener("scroll", this.onScroll)
        document.addEventListener("click", this.handleOutsideClick)
    }

    componentWillUnmount() {
        window.removeEventListener("scroll", this.onScroll)
        document.removeEventListener("click", this.handleOutsideClick)
        if (this.chapterObserver) {
            this.chapterObserver.disconnect()
        }
        if (this.paragraphObserver) {
            this.paragraphObserver.disconnect()
        }
        if (this.scrollRAF) {
            cancelAnimationFrame(this.scrollRAF)
        }
    }

    detectChapters = () => {
        const chapters: string[] = []
        const chapterElements: HTMLElement[] = []

        const nodes = document.querySelectorAll("h3.chatper_title, h3.chapter-title")
        nodes.forEach((el: HTMLElement) => {
            const text = (el.innerText || el.textContent || "").trim()
            if (text) {
                chapters.push(text)
                chapterElements.push(el)
            }
        })

        if (chapters.length === 0 && this.retryCount < 10) {
            this.retryCount++
            setTimeout(() => this.detectChapters(), 500)
            return
        }

        this.setState({ chapters, chapterElements }, () => {
            if (chapters.length > 0) {
                this.setupChapterObserver()
                this.detectCurrentChapter()
                this.onScroll()
            }
        })
    }

    setupChapterObserver = () => {
        this.chapterObserver = new IntersectionObserver(
            (entries) => {
                if (this.disableObserver) return
                const visible = entries
                    .filter((e) => e.isIntersecting && (e.target as HTMLElement).innerText.trim())
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)

                if (visible.length > 0) {
                    const text = (visible[0].target as HTMLElement).innerText.trim()
                    const idx = this.state.chapters.indexOf(text)
                    if (idx !== -1 && idx !== this.state.currentChapterIndex) {
                        this.setState({ currentChapterIndex: idx }, () => {
                            this.scrollSidebarToCurrentChapter()
                        })
                    }
                }
            },
            { rootMargin: "-80px 0px -66% 0px", threshold: 0 } 
        )

        this.state.chapterElements.forEach((el) => this.chapterObserver.observe(el))
    }

    detectCurrentChapter = () => {
        const { chapterElements } = this.state
        if (chapterElements.length === 0) return
        let currentIndex = -1
        for (let i = chapterElements.length - 1; i >= 0; i--) {
            if (chapterElements[i].getBoundingClientRect().top <= window.innerHeight * 0.3) {
                currentIndex = i
                break
            }
        }
        if (currentIndex >= 0) {
            this.setState({ currentChapterIndex: currentIndex }, () => {
                this.scrollSidebarToCurrentChapter()
            })
        }
    }

    scrollSidebarToCurrentChapter = () => {
        const { currentChapterIndex } = this.state
        if (!this.sidebarContentRef || currentChapterIndex < 0) return

        const sidebarItems = this.sidebarContentRef.querySelectorAll(".vtoc-sidebar-item")
        const activeItem = sidebarItems[currentChapterIndex] as HTMLElement | null
        
        if (activeItem) {
            const sidebarScrollTop = this.sidebarContentRef.scrollTop
            const itemTop = activeItem.offsetTop
            const itemHeight = activeItem.offsetHeight
            const sidebarHeight = this.sidebarContentRef.offsetHeight
            
            const targetScroll = itemTop - (sidebarHeight / 2) + (itemHeight / 2)
            this.sidebarContentRef.scrollTop = Math.max(0, targetScroll)
        }
    }

    onScroll = () => {
        if (this.scrollRAF) cancelAnimationFrame(this.scrollRAF)
        this.scrollRAF = requestAnimationFrame(() => {
            const { chapterElements } = this.state
            if (chapterElements.length === 0) return

            const currentScrollY = window.scrollY
            const isScrollingUp = currentScrollY < this.lastScrollY
            
            // Trigger paragraph observer check if scrolling upward
            if (isScrollingUp) {
                this.checkParagraphsForUpwardScroll()
            }
            
            this.lastScrollY = currentScrollY

            const articleBody = document.querySelector("#ArticleBodyComponent") as HTMLElement | null
            let progress = 0
            let firstEl: HTMLElement | undefined
            if (articleBody) {
                const startY = articleBody.getBoundingClientRect().top + window.scrollY
                const endY = startY + articleBody.offsetHeight
                const totalHeight = endY - startY
                const scrolled = window.scrollY - startY + window.innerHeight
                progress = Math.max(0, Math.min(100, (scrolled / totalHeight) * 100))
            } else {
                // Fallback to chapter-based calculation if article body not found
                firstEl = chapterElements[0]
                const lastEl = chapterElements[chapterElements.length - 1]
                const startY = firstEl.getBoundingClientRect().top + window.scrollY
                const endY = lastEl.getBoundingClientRect().top + window.scrollY + lastEl.offsetHeight
                const totalHeight = endY - startY
                const scrolled = window.scrollY - startY + window.innerHeight
                progress = Math.max(0, Math.min(100, (scrolled / totalHeight) * 100))
            }

            const { siteLayoutType } = this.props
            const isMobile = siteLayoutType === "mobileApp" || siteLayoutType === "mobileWeb"
            let isBarVisible = false
            if (isMobile) {
                isBarVisible = this.topStoryEl ? this.topStoryEl.getBoundingClientRect().bottom <= 0 : firstEl ? firstEl.getBoundingClientRect().top <= 223 + 67 : false
            } else if (this.topStoryEl) {
                isBarVisible = this.topStoryEl.getBoundingClientRect().bottom <= 0
            } else if (this.containerRef) {
                isBarVisible = this.containerRef.getBoundingClientRect().top <= 55
            } else {
                isBarVisible = firstEl ? firstEl.getBoundingClientRect().top < window.innerHeight * 0.3 : false
            }

            if (!isBarVisible && this.state.isMobileDropdownOpen) {
                this.setState({ readProgress: progress, isBarVisible: isBarVisible, isMobileDropdownOpen: false })
            } else {
                this.setState({ readProgress: progress, isBarVisible: isBarVisible })
            }
        })
    }

    checkParagraphsForUpwardScroll = () => {
        if (this.disableObserver) return

        const allChapters = document.querySelectorAll("h3.chatper_title, h3.chapter-title")
        if (allChapters.length === 0) return

        // Detection zone: top 40% of visible viewport
        const detectionZone = window.innerHeight * 0.4
        let foundChapterIndex = -1

        for (let j = 0; j < allChapters.length; j++) {
            const chapterEl = allChapters[j] as HTMLElement
            const chapterRect = chapterEl.getBoundingClientRect()

            if (chapterRect.top < detectionZone && chapterRect.bottom > 0) {
                foundChapterIndex = j
                break
            }
        }

        if (foundChapterIndex === -1) {
            const paragraphs = document.querySelectorAll(".text_editor_paragraph")
            if (paragraphs.length > 0) {
                for (let i = 0; i < paragraphs.length; i++) {
                    const paragraph = paragraphs[i] as HTMLElement
                    const rect = paragraph.getBoundingClientRect()

                    // Only check paragraphs that are visible in the top detection zone
                    if (rect.top < detectionZone && rect.bottom > 0) {
                        for (let j = this.state.chapters.length - 1; j >= 0; j--) {
                            const chapterEl = allChapters[j] as HTMLElement
                            if (!chapterEl) continue

                            // Check if chapter appears BEFORE this paragraph in DOM
                            const comparison = chapterEl.compareDocumentPosition(paragraph)
                            if (comparison & Node.DOCUMENT_POSITION_FOLLOWING) {
                                foundChapterIndex = j
                                break
                            }
                        }

                        if (foundChapterIndex !== -1) {
                            break
                        }
                    }
                }
            }
        }


        if (foundChapterIndex !== this.state.currentChapterIndex) {
            if (foundChapterIndex !== -1) {
                this.setState({ currentChapterIndex: foundChapterIndex }, () => {
                    this.scrollSidebarToCurrentChapter()
                })
            } else {
                //chapter 1 → clear the active chapter name
                const firstChapterEl = this.state.chapterElements[0]
                if (firstChapterEl && firstChapterEl.getBoundingClientRect().top > 0) {
                    this.setState({ currentChapterIndex: -1 })
                }
            }
        }
    }

    getVisibleTaboolaReadMoreButton = (): HTMLElement | null => {
        const btn = document.querySelector(".tbl-read-more-btn") as HTMLElement | null
        if (!btn) return null

        const style = window.getComputedStyle(btn)
        if (style.display === "none" || style.visibility === "hidden") return null

        const rect = btn.getBoundingClientRect()
        if (rect.width === 0 && rect.height === 0) return null

        return btn
    }

    expandTaboolaReadMoreIfNeeded = () => {
        const btn = this.getVisibleTaboolaReadMoreButton()
        if (btn) btn.click()
    }

    scrollToChapter = (index: number) => {
        const { chapterElements } = this.state
        const { siteLayoutType } = this.props
        const el = chapterElements[index]
        if (!el) return

        const isMobile = siteLayoutType === "mobileApp" || siteLayoutType === "mobileWeb"
        const offset = isMobile ? 120 : 130
        const y = window.scrollY + el.getBoundingClientRect().top - offset
        window.scrollTo({ top: y, behavior: "smooth" })
    }

    handleChapterClick = (index: number) => {
        const { chapterElements, chapters } = this.state
        const { siteLayoutType } = this.props
        const el = chapterElements[index]
        if (!el) return

        this.disableObserver = true
        this.setState({
            currentChapterIndex: index,
            isMobileExpanded: false,
            isMobileDropdownOpen: false,
            isDesktopSidebarOpen: false,
        })

        // Expand Taboola "read more" if present, then scroll on next frame
        // so layout updates without a noticeable delay.
        this.expandTaboolaReadMoreIfNeeded()
        requestAnimationFrame(() => {
            this.scrollToChapter(index)
        })

        setTimeout(() => {
            this.disableObserver = false
        }, 1000)

        if (siteLayoutType === "mobileApp" && typeof window !== "undefined") {
            const text = "componenta_name=vertical_chapter_menu&content_type=menu&click_text=" + chapters[index]
            if (window.webkit && window.webkit.messageHandlers) {
                window.webkit.messageHandlers.onClickChapter.postMessage(text)
            }
            if (window.Android) {
                window.Android.onClickChapter(text)
            }
        }
    }

    toggleMobilePanel = () => {
        this.setState((prev) => ({ isMobileExpanded: !prev.isMobileExpanded }))
    }

    toggleMobileDropdown = () => {
        this.setState((prev) => ({ isMobileDropdownOpen: !prev.isMobileDropdownOpen }))
    }

    handleOutsideClick = (e: MouseEvent) => {
        if (!this.state.isMobileDropdownOpen) return
        const sidebar = document.querySelector(".vtoc-sidebar")
        const progressBar = document.querySelector(".vtoc-progress-bar")
        if (sidebar && sidebar.contains(e.target as Node)) return
        if (progressBar && progressBar.contains(e.target as Node)) return
        this.setState({ isMobileDropdownOpen: false })
    }

    toggleDesktopSidebar = () => {
        this.setState((prev) => ({ isDesktopSidebarOpen: !prev.isDesktopSidebarOpen }))
    }

    getChapterStatus = (index: number): string => {
        const { currentChapterIndex } = this.state
        if (currentChapterIndex < 0) return "upcoming"
        if (index < currentChapterIndex) return "completed"
        if (index === currentChapterIndex) return "active"
        return "upcoming"
    }

    renderDesktop() {
        const { chapters, currentChapterIndex, isBarVisible, isDesktopSidebarOpen } = this.state
        const currentTitle = currentChapterIndex >= 0 ? chapters[currentChapterIndex] : ""

        return (
            <div className={`article-vertical-chapter-menu ${chapters.length > 0 ? "" : "noChapters"}`}>
                <div className={classNames("vtoc-progress-bar", { visible: isBarVisible })} onClick={this.toggleDesktopSidebar}>
                    <div className="vtoc-progress-content">
                        <span className="vtoc-current-title"><span className="read-current-chapter">בקריאה</span>{currentTitle}</span>
                        <span className={classNames("vtoc-bar-chevron", { open: isDesktopSidebarOpen })} />
                    </div>
                </div>

                <div className={classNames("vtoc-sidebar", { visible: isBarVisible, "sidebar-open": isDesktopSidebarOpen })}>
                    <div className="vtoc-sidebar-title">פרקי הכתבה</div>
                    <div className={classNames("vtoc-sidebar_content", { visible: isBarVisible, "sidebar-open": isDesktopSidebarOpen })} ref={(el) => { this.sidebarContentRef = el }}>
                    <div className="vtoc-sidebar-list">
                        {chapters.map((chapter, i) => (
                            <div
                                key={i}
                                className={classNames("vtoc-sidebar-item", this.getChapterStatus(i))}
                                onClick={() => this.handleChapterClick(i)}
                            >
                                <div className="vtoc-dot-col">
                                    <div className="vtoc-dot" />
                                    {i < chapters.length - 1 && <div className="vtoc-connector" />}
                                </div>
                                <div className="vtoc-label">
                                    <span className="vtoc-title">{chapter}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    </div>
                </div>
            </div>
        )
    }

    renderMobile() {

        const { chapters, currentChapterIndex, readProgress, isBarVisible, isMobileDropdownOpen } = this.state
        const currentTitle = currentChapterIndex >= 0 ? chapters[currentChapterIndex] : ""

        return (
              <div className={`article-vertical-mobile-chapter-menu ${chapters.length > 0 ? "" : "noChapters"}`}>
                <div  
                    className={classNames("vtoc-progress-bar", { visible: isBarVisible, "dropdown-open": isMobileDropdownOpen })}
                    onClick={this.toggleMobileDropdown} >
                    <div className={classNames("vtoc-progress-track", { visible: isBarVisible })} >
                        <div className="vtoc-progress-fill" style={{ width: readProgress + "%" }} />
                    </div> 
                    <div className="vtoc-progress-content">
                        <div className="vtoc-current-title">
                            <div className="read-current-chapter">בקריאה:</div>
                            <div className="chapter">{currentTitle}</div>
                        </div>
                        <span className={classNames("vtoc-bar-chevron", { open: isMobileDropdownOpen })} />
                    </div>
                </div>

                <div className={classNames("vtoc-sidebar", { visible: isBarVisible, "dropdown-open": isMobileDropdownOpen })}>
                    <div className="vtoc-sidebar-title">פרקי הכתבה:</div>
                    <div className="vtoc-sidebar-list">
                        {chapters.map((chapter, i) => (
                            <div
                                key={i}
                                className={classNames("vtoc-sidebar-item", this.getChapterStatus(i))}
                                onClick={() => this.handleChapterClick(i)}
                            >
                                <div className="vtoc-dot-col">
                                    <div className="vtoc-dot" />
                                    {i < chapters.length - 1 && <div className="vtoc-connector" />}
                                </div>
                                <div className="vtoc-label">
                                    <span className="vtoc-title">{chapter}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )
    }

    render() {
        const { siteLayoutType } = this.props
        const isMobile = siteLayoutType === "mobileApp" || siteLayoutType === "mobileWeb"
        return isMobile ? this.renderMobile() : this.renderDesktop()
    }
}
